/**
 * WOOF Delivery Time
 * Computes a delivery date window from data attributes:
 * - data-min-days / data-max-days: business-day offsets from today
 * - data-cutoff: hour of day after which counting starts tomorrow
 * - data-skip-weekends: skip Sat/Sun while counting
 * Also renders a live "order within HH:MM:SS" cutoff countdown.
 */
class WoofDeliveryTime extends HTMLElement {
  connectedCallback() {
    this.datesEl = this.querySelector('[data-dates]');
    this.countdownEl = this.querySelector('[data-countdown]');
    this.#renderDates();
    if (this.countdownEl) {
      this.#tick();
      this.timer = setInterval(() => this.#tick(), 1000);
    }
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
  }

  #renderDates() {
    if (!this.datesEl) return;
    const minDays = Number(this.dataset.minDays || 3);
    const maxDays = Number(this.dataset.maxDays || 7);
    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const from = this.#addDays(this.#startDate(), minDays);
    const to = this.#addDays(this.#startDate(), Math.max(minDays, maxDays));
    this.datesEl.textContent = `${format.format(from)} – ${format.format(to)}`;
  }

  #startDate() {
    const start = new Date();
    const cutoff = Number(this.dataset.cutoff ?? 23);
    if (start.getHours() >= cutoff) start.setDate(start.getDate() + 1);
    return start;
  }

  #addDays(from, days) {
    const skipWeekends = this.hasAttribute('data-skip-weekends');
    const date = new Date(from);
    let remaining = days;
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (skipWeekends && (date.getDay() === 0 || date.getDay() === 6)) continue;
      remaining -= 1;
    }
    return date;
  }

  #tick() {
    const cutoff = Number(this.dataset.cutoff ?? 23);
    const now = new Date();
    const target = new Date(now);
    target.setHours(cutoff, 0, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    const total = Math.floor((target.getTime() - now.getTime()) / 1000);
    const hours = String(Math.floor(total / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const seconds = String(total % 60).padStart(2, '0');
    this.countdownEl.textContent = `${hours}:${minutes}:${seconds}`;
  }
}

if (!customElements.get('woof-delivery-time')) {
  customElements.define('woof-delivery-time', WoofDeliveryTime);
}
